"use client";

import { useCurrentUser } from "@/hooks/use-current-user";
import { useIntercom } from "@/hooks/use-intercom";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import MessageSquare from "@repo/icons/react/MessageSquare";
import { X } from "lucide-react";

type IntercomCTAContext = "seller" | "broker" | "general";

const CONTEXT_CONFIG: Record<
  IntercomCTAContext,
  { label: string; message: string }
> = {
  seller: {
    label: "Talk to us about selling",
    message:
      "Hi, I'm interested in selling my business and would love to learn more.",
  },
  broker: {
    label: "Talk to us about growing your brokerage",
    message: "Hi, I'm a broker interested in growing my practice with Venturu.",
  },
  general: {
    label: "Have a question?",
    message: "Hi, I have a question about Venturu.",
  },
};

/**
 * Maps pathname prefixes to the CTA context. Returns `null` for pages where
 * the floating CTA should not appear.
 */
function getContextForPath(pathname: string): IntercomCTAContext | null {
  if (
    pathname === "/sell-your-business" ||
    pathname.startsWith("/free-business-valuation")
  ) {
    return "seller";
  }

  if (
    pathname === "/for-brokers" ||
    pathname.startsWith("/for-brokers/") ||
    pathname === "/social-ads"
  ) {
    return "broker";
  }

  if (pathname === "/") {
    return "general";
  }

  return null;
}

/** Delay in ms before the CTA fades in on page load. */
const ENTRANCE_DELAY_MS = 2500;

export function MarketingIntercomCTA() {
  const pathname = usePathname();
  const { showNewMessage } = useIntercom();
  const { loaded } = useCurrentUser();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const context = getContextForPath(pathname);

  // Fade in after a short delay so it doesn't compete with the hero section
  useEffect(() => {
    if (!context || !loaded) {
      setVisible(false);
      return;
    }

    // Reset dismissed state when navigating to a new page
    setDismissed(false);

    const timer = setTimeout(() => {
      setVisible(true);
    }, ENTRANCE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [context, loaded]);

  if (!context || dismissed) {
    return null;
  }

  const config = CONTEXT_CONFIG[context];

  return (
    <div
      className={cn(
        "fixed bottom-6 right-6 z-50 transition-all duration-500 ease-out",
        "max-w-[340px]",
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0",
      )}
    >
      <div className="relative flex items-center gap-3 rounded-lg bg-foreground px-4 py-3 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl md:px-5 md:py-3.5">
        {/* Dismiss button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
          }}
          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-card text-muted-foreground shadow-md transition-colors hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        {/* Chat icon with online indicator */}
        <button
          type="button"
          onClick={() => showNewMessage(config.message)}
          className="flex w-full items-center gap-3"
          aria-label={config.label}
        >
          <div className="relative flex-shrink-0">
            <MessageSquare className="h-5 w-5 text-white md:h-6 md:w-6" />
            {/* Online dot */}
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-background bg-success" />
          </div>

          <div className="flex flex-col text-left">
            <span className="text-sm font-semibold leading-tight text-white md:text-[15px]">
              {config.label}
            </span>
            <span className="text-xs text-background/80">We're online</span>
          </div>
        </button>
      </div>
    </div>
  );
}
